'use client';
import { useEffect, useRef } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import Link from 'next/link';

import { AddToCartButton } from '@/modules/cart/components/AddToCartButton';
import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import Image from '@/modules/common/components/Image';
import { Eye, Heart } from '@/modules/common/components/icons';
import { SkeletonDark } from '@/modules/common/components/skeleton';
import { useLocalStorage } from '@/modules/common/hooks/useLocalStorage';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { useWaitForDataLayerEvent } from '@/modules/common/hooks/useWaitForDataLayerEvent';
import { ITEMS_ON_CART } from '@/modules/sample-card';
import { AddToShoppingListButton } from '@/modules/shopping-list/components/AddToShoppingListButton';
import { ShoppingListSaveType } from '@/modules/shopping-list/hooks/useShoppingListActions';
import { selectShoppingListProducts } from '@/modules/shopping-list/state/shoppingListSlice';

import { useCollectionRoomSelector } from '../../context/CollectionRoomContext';
import { useQuickView } from '../../context/QuickViewContext';
import { ProductPdpInterface, ProductWithIndex } from '../../models/product-pdp.interface';
import { ALGOLIA_SEARCH_INDEX } from '../../services/consts';
import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '../../services/data-layer-events/add-to-wishlist';
import { selectItemDataLayerEvent } from '../../services/data-layer-events/select-item';
import { viewItemListDataLayerEvent } from '../../services/data-layer-events/view-item-list';
import { SamplePrice } from '../sample/SamplePrice';

export const CollectionRoomSelectedProduct = () => {
  const productCollection = useCollectionRoomSelector('productCollection');
  const exactMatchProducts = useCollectionRoomSelector('exactMatchProducts');
  const collection = useCollectionRoomSelector('collection');
  const isLoadingProducts = useCollectionRoomSelector('isLoadingProducts');

  const index = exactMatchProducts.findIndex((product) => product.sku === productCollection?.sku);

  const product = productCollection?.fullProduct;
  const [, setItemsOnCart] = useLocalStorage<ProductWithIndex[]>(ITEMS_ON_CART, []);
  const shoppingListProducts = useAppSelector(selectShoppingListProducts);
  const { openModal } = useQuickView();
  const productRef = useRef<HTMLElement>(null);
  const prevProductRef = useRef<string>(null);
  const virtualPageViewLoaded = useWaitForDataLayerEvent('virtual_page_view', {
    timeout: 5000,
  });
  const { renderState } = useInstantSearch();
  const abTestID = renderState[ALGOLIA_SEARCH_INDEX]?.hits?.results?.abTestID;
  const abTestVariantID = renderState[ALGOLIA_SEARCH_INDEX]?.hits?.results?.abTestVariantID;

  const surfaces = productCollection?.surfaces?.map((surface) => surface.label).join(', ');

  useEffect(() => {
    if (
      !productRef.current ||
      !virtualPageViewLoaded.eventOccurred ||
      (product && prevProductRef.current === product.sku)
    ) {
      return;
    }

    viewItemListDataLayerEvent({
      shoppingListProducts,
      feature: DataLayerFeature.Collections,
      where: DataLayerWhere.Hero,
      products: [
        {
          ...(product as ProductPdpInterface),
          index,
        },
      ],
      item_list_id: collection?.slug || '',
      item_list_name: collection?.collectionName || '',
      item_list_type: 'Room collection',
      searchTestId: abTestID,
      searchTestVariantId: abTestVariantID,
    });

    prevProductRef.current = product?.sku || null;
  });

  const handleAddToCart = () => {
    setItemsOnCart((prev) => [
      ...prev,
      {
        ...product,
        index,
      } as ProductWithIndex,
    ]);
  };

  const handleQuickView = () => {
    openModal(product as ProductPdpInterface, {
      where: DataLayerWhere.Hero,
      item_list_id: collection.slug ?? '',
      item_list_name: collection.collectionName ?? '',
      item_list_type: 'Room collection',
    });

    selectItemDataLayerEvent({
      shoppingListProducts,
      where: DataLayerWhere.Hero,
      feature: DataLayerFeature.Collections,
      item_list_id: collection.slug ?? '',
      item_list_name: collection.collectionName ?? '',
      item_list_type: 'Room collection',
      product: product as ProductPdpInterface,
      index: index,
    });
  };

  const wishlistTrackingCallback = ({ shoppingListId }: { shoppingListId?: string }) => {
    addToWishlistDataLayerEvent({
      feature: DataLayerFeature.Collections,
      where: DataLayerWhere.Hero,
      method: 'Single',
      type: ShoppingListSaveType.Product,
      product: product as ProductPdpInterface,
      shopping_list_id: shoppingListId ?? null,
      shoppingListProducts: null,
      item_list_id: collection.slug,
      item_list_name: collection.collectionName,
      item_list_type: 'Room collection',
      exactMatch: true,
    });
  };

  if (isLoadingProducts) {
    return <SelectedProductSkeleton />;
  }

  if (!product) {
    return null;
  }

  return (
    <section className="text-left" ref={productRef}>
      <Link href={`/sample/${product.sku}/${product.ecommName}`}>
        {surfaces && (
          <h2 className="mb-3 text-base">
            Applied to: <span className="text-bold">{surfaces}</span>
          </h2>
        )}
        <div className="flex items-center gap-4">
          <Image
            className="block h-20 w-20 overflow-hidden rounded-lg object-cover"
            src={product?.images?.[0]?.url ?? ''}
            alt={productCollection.name}
            style={{
              boxShadow:
                '0px 3px 6px 0px #0000001A, 0px 11px 11px 0px #00000017, 0px 24px 14px 0px #0000000D, 0px 43px 17px 0px #00000003, 0px 67px 19px 0px #00000000',
            }}
          />

          <div className="flex-1">
            <h2
              className="max-w-1/3 whitespace-initial mb-1 overflow-hidden text-ellipsis text-xs leading-4 text-gray-500"
              data-testid="sample-card-brand-name"
            >
              {product.subCategory?.name} {product?.category?.name}
            </h2>
            <h3
              className="whitespace-initial overflow-hidden text-ellipsis leading-5"
              data-testid="sample-card-collection-name"
            >
              <strong>{product.brandName}</strong> {product.name}
            </h3>
            <p className={`align-self-end mb-3 text-[13px] leading-4 text-gray-500 lg:mb-[10px]`}>
              <strong className="text-gray-800">
                <SamplePrice priceInCents={product.pricePerUnit} />
              </strong>{' '}
              per sample
            </p>
          </div>

          <AddToShoppingListButton
            quantity={1}
            sku={product.sku}
            variant="white"
            image={product?.images?.[0]?.url ?? ''}
            color={product.color}
            brandSku={product.brandSku}
            callbacks={{ afterAdd: wishlistTrackingCallback }}
            brandId={product.brandId}
            saveType={ShoppingListSaveType.Product}
            className="static"
            data-testid="add-to-shopping-list-button"
          >
            <Heart className="p-1" />
          </AddToShoppingListButton>
        </div>
      </Link>

      <footer className="flex gap-3 pt-6">
        <Button onClick={handleQuickView} size="m" variant="white">
          <Eye className="h-6 w-6" />
          Quick view
        </Button>

        <AddToCartButton
          where={DataLayerWhere.Hero}
          feature={DataLayerFeature.Collections}
          isOnStock={product.isOnStock}
          onAddProduct={handleAddToCart}
          product={product}
          price={product.pricePerUnit}
          showPrice={false}
          itemListType={'Room collection'}
          itemListName={collection.collectionName ?? ''}
          itemListId={collection.slug ?? ''}
          dataLayerIndex={index}
        />
      </footer>
    </section>
  );
};

const SelectedProductSkeleton = () => (
  <>
    <SkeletonDark className="mb-3 h-6 w-52" />

    <div className="flex gap-4">
      <SkeletonDark className="h-20 w-20" />
      <div className="flex flex-col gap-1">
        <SkeletonDark className="h-4 w-20" />
        <SkeletonDark className="h-6 w-40" />
        <SkeletonDark className="h-4 w-14" />
      </div>
    </div>

    <div className="flex gap-3 pt-6">
      <SkeletonDark className="h-9 w-36" />
      <SkeletonDark className="h-9 w-full" />
    </div>
  </>
);
